'use client'

import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { FaCanadianMapleLeaf } from 'react-icons/fa'
import { RouteLink } from '@/lib/router'
import { SOCIAL_LINKS } from '@/content/social'
import logo from '@/assets/logo-footer.png'

const footerLinks = {
  Studio: [
    { label: 'Work', href: '/portfolio' },
    { label: 'Web design', href: '/services/web-design' },
    { label: 'Custom software', href: '/services/custom-software' },
    { label: 'Brand', href: '/services/graphic-design' },
    { label: 'Contact', href: '/contact' },
    { label: 'Connect', href: '/connect' },
  ],
  Company: [
    { label: 'About', href: '/#about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Privacy', href: '/privacy-policy' },
  ],
}

const socialLinks = SOCIAL_LINKS

export function Footer() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <footer
      ref={ref}
      className="py-20 md:py-32 px-6 lg:px-12 border-t border-white/10 bg-neutral-950 text-neutral-100"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-16 mb-20">
          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <RouteLink to="/" className="inline-block mb-6">
              <img
                src={logo}
                alt="Elementary Creative"
                className="h-[3.3rem] w-auto md:h-[3.8rem]"
              />
            </RouteLink>
            <p className="text-base md:text-lg text-neutral-400 leading-relaxed mb-8 max-w-md">
              Websites and custom business software for teams that need tools that perform in
              production.
            </p>
            <div className="flex flex-wrap gap-6">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block py-3 text-base text-neutral-400 hover:text-accent transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </motion.div>

          <div className="md:col-span-7 grid grid-cols-2 gap-8">
            {Object.entries(footerLinks).map(([category, links], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.1 + index * 0.05 }}
              >
                <h3 className="text-sm tracking-[0.2em] uppercase text-neutral-500 mb-6">
                  {category}
                </h3>
                <ul className="space-y-0">
                  {links.map((link) => (
                    <li key={link.label}>
                      <RouteLink
                        to={link.href}
                        className="inline-block py-3 text-base text-neutral-300 hover:text-accent transition-colors"
                      >
                        {link.label}
                      </RouteLink>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-8 border-t border-white/10"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex flex-wrap items-center gap-4 md:gap-8 text-sm text-neutral-500">
            <span>© {new Date().getFullYear()} Elementary Creative</span>
            <RouteLink to="/privacy-policy" className="inline-block py-3 hover:text-accent transition-colors">
              Privacy Policy
            </RouteLink>
          </div>
          <motion.div className="flex items-center gap-1.5 text-sm text-neutral-500">
            <FaCanadianMapleLeaf className="text-red-500" size={15} aria-hidden />
            Canada ·{' '}
            <a href="tel:+16393164005" className="inline-block py-3 hover:text-accent">
              (639) 316-4005
            </a>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  )
}
